import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/auth/AuthContext";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { syncService } from "@/services/sync.service";
import { waitForSignOut } from "@/context/auth/AuthStateListener";

// Pagina dedicata alla disconnessione. Mostra un messaggio di
// feedback all'utente e attende un breve countdown prima del
// reindirizzamento alla schermata di login. La scelta del countdown
// è pensata per dare un chiaro segnale di stato senza bruschi cambi.
const LogoutPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme } = useThemeConfig();
  const [seconds, setSeconds] = useState(3);
  const [cleanupDone, setCleanupDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Avvia il processo di logout e di pulizia locale
  useEffect(() => {
    let active = true;
    const performLogout = async () => {
      // Evita ulteriori sincronizzazioni mentre ci si disconnette
      syncService.disablePeriodicSync();

      // Prepara la promise che segnala il completamento del cleanup
      const signOutPromise = waitForSignOut();

      try {
        // Esegui la disconnessione
        await logout();

        // Attende che l'AuthStateListener completi la sua pulizia
        await signOutPromise;
      } catch (err) {
        console.error("Errore durante il logout", err);
        if (active) {
          setError(
            "Si è verificato un problema durante la disconnessione. Verrai reindirizzato."
          );
        }
      } finally {
        if (active) {
          setCleanupDone(true);
        }
      }
    };

    performLogout();
    return () => {
      active = false;
    };
  }, [logout]);

  // Gestisce il countdown visivo per feedback all'utente
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Reindirizza al login quando il countdown termina.
  // Se il cleanup non è completato, si procede comunque ma si mostra un avviso.
  useEffect(() => {
    if (seconds === 0) {
      if (!cleanupDone) {
        console.warn("Logout cleanup incomplete, forcing redirect");
      }
      navigate("/login", { replace: true });
    }
  }, [seconds, cleanupDone, navigate]);

  return (
    <div
      className="min-viewport-height flex flex-col items-center justify-center p-6"
      style={{
        backgroundColor: theme.backgroundColor,
        color: theme.textColor,
      }}
    >
      <h1
        className="text-2xl font-bold mb-2"
        style={{ color: theme.sectionTitleColor }}
      >
        Disconnessione in corso…
      </h1>
        <p style={{ color: theme.sectionTextColor }}>
          Verrai reindirizzato in {seconds}s
        </p>
        {error && (
          <p
            className="mt-4 text-sm"
            style={{ color: theme.errorColor }}
            role="alert"
            aria-live="assertive"
          >
            {error}
          </p>
        )}
        {!error && !cleanupDone && seconds === 0 && (
          <p
            className="mt-4 text-sm"
            style={{ color: theme.sectionTextColor }}
            role="status"
            aria-live="polite"
          >
            Pulizia dell'account in corso, attendi qualche secondo...
          </p>
        )}
      </div>
    );
  };

export default LogoutPage;

